<template>
    <v-data-table
        class="elevation-0"
        :headers="headers"
        :items="items"
        :loading="isBusy"
        :loading-text="$t('common.loading_table_data')"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="dataTableFooterProps"
        :expanded.sync="expanded"
        show-expand
    >
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="filter"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
                <v-spacer/>
                <p v-if="synchronization && synchronization.status === 'error' && synchronization.error" class="mr-3 mt-2 error--text">
                    {{ synchronization.error }}
                </p>
                <v-btn
                    color="primary"
                    dark
                    class="mb-2 mr-3"
                    @click="onSynchronize"
                    :loading="isBusySynchronize || (synchronization && (synchronization.status === 'waiting' || synchronization.status === 'synchronizing'))"
                >
                    {{ $t('button.synchronize') }}
                </v-btn>
                <v-dialog v-model="dialog" max-width="800">
                    <template v-slot:activator="{ on }">
                        <v-btn
                            v-on="on"
                            color="success"
                            class="mb-2"
                            dark
                        >
                            {{ $t('bulk_orders_import.create_bulk_order_import') }}
                        </v-btn>
                    </template>
                    <create-bulk-order-import-stepper
                        :warehouse-id="warehouseId"
                        @onClose="onDialogClose"
                    />
                </v-dialog>
            </v-toolbar>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
                <create-bulk-order-import-statistics :import-id="item.id" class="ma-5"/>
            </td>
        </template>
        <template v-slot:item.created_at="{ item }">
            {{ $moment(item.created_at).format('LLL') }}
        </template>
        <template v-slot:item.processed_at="{ item }">
            {{ item.processed_at ? $moment(item.processed_at).format('LLL') : '-' }}
        </template>
        <template v-slot:item.status="{ item }">
            <v-tooltip bottom :disabled="!item.error">
                <template v-slot:activator="{ on }">
                    <v-chip small :color="statusColor(item.status)" v-on="on">
                        {{ $t("bulk_orders_import.status." + item.status) }}
                    </v-chip>
                </template>
                <span>{{ item.error }}</span>
            </v-tooltip>
        </template>
        <template v-slot:item.warehouse="{ item }">
            {{ item.warehouse.name }}
        </template>
        <template v-slot:item.actions="{ item }">
            <span class="text-no-wrap">
                  <v-tooltip bottom v-if="['dry-run-finished'].includes(item.status)">
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onProcess(item)" v-on="on" class="mr-2">
                            mdi-play
                        </v-icon>
                    </template>
                    <span>{{ $t('bulk_orders_import.process') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onDelete(item)" v-on="on">
                            mdi-delete
                        </v-icon>
                    </template>
                    <span>{{ $t('bulk_orders_import.delete') }}</span>
                </v-tooltip>
            </span>
        </template>
    </v-data-table>
</template>

<script>
import {API} from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin.vue";
import DataTableMixin from "@/mixins/DataTableMixin";
import CreateBulkOrderImportStepper from "@/components/user/bulkOrdersImport/CreateBulkOrderImportStepper.vue";
import CreateBulkOrderImportStatistics from "@/components/user/bulkOrdersImport/CreateBulkOrderImportStatistics.vue";
import _ from "lodash";

export default {
    name: "BulkOrderImportTable",
    mixins: [SnackbarMixin, DataTableMixin],
    components: {CreateBulkOrderImportStatistics, CreateBulkOrderImportStepper},
    props: {
        warehouseId: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        // loading
        isBusyImports: false,
        isBusyDelete: false,
        isBusySynchronize: false,
        isBusySynchronization: false,
        isBusyProcess: false,
        // create bulk orders import dialog
        dialog: false,
        // table data
        items: [],
        // table filter
        filter: "",
        // data table pagination options (sort by ID in descending order by default)
        options: {
            page: 1,
            itemsPerPage: 15,
            sortBy: ["id"],
            sortDesc: [true]
        },
        // data table total items
        total: 0,
        // expandable table rows
        expanded: [],
        // synchronization data
        synchronization: null
    }),
    watch: {
        options: {
            handler() {
                this.fetchImports()
            },
            deep: true
        },
        filter: {
            handler: _.debounce(function () {
                this.resetServerTablePage(this.fetchImports);
            }, 250)
        }
    },
    computed: {
        isBusy() {
            return this.isBusyImports || this.isBusyDelete || this.isBusySynchronize || this.isBusySynchronization || this.isBusyProcess;
        },
        headers() {
            return [
                {
                    text: this.$t('bulk_orders_import.id'),
                    value: 'id',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.external_id'),
                    value: 'external_id',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.created_at'),
                    value: 'created_at',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.processed_at'),
                    value: 'processed_at',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.type'),
                    value: 'type',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.status.status'),
                    value: 'status',
                    sortable: true
                },
                {
                    text: this.$t('bulk_orders_import.warehouse'),
                    value: 'warehouse',
                    sortable: false
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        }
    },
    methods: {
        fetchImports() {
            const {sortBy, sortDesc, page, itemsPerPage} = this.options;
            const sortMap = {
                external_id: "externalId",
                created_at: "createdAt",
                processed_at: "processedAt"
            };
            this.isBusyImports = true;
            API.get('/api/bulk-order-imports', {
                params: {
                    page: page,
                    perPage: itemsPerPage,
                    itemsPerPage: itemsPerPage,
                    sortBy: this.mapServerSort(sortBy, sortMap, "id"),
                    sortDesc: (_.isArray(sortDesc) ? (sortDesc[0] ? "true" : "false") : "false"),
                    filter: this.filter || null
                }
            })
                .then(response => {
                    this.items = response.data.items;
                    this.total = response.data.total;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyImports = false;
                });
        },
        fetchSynchronization() {
            this.isBusySynchronization = true;
            API.get('/api/synchronizations/bulk-order-import')
                .then(response => {
                    this.synchronization = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySynchronization = false;
                });
        },
        onSynchronize() {
            this.isBusySynchronize = true;
            API.post('/api/bulk-order-imports/synchronize', [])
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.bulk_order_import.synchronized'));
                    this.fetchImports();
                    this.fetchSynchronization();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySynchronize = false;
                });
        },
        onProcess(item) {
            if (!confirm(this.$t('confirm.process_bulk_external_order_import'))) {
                return;
            }

            this.isBusyProcess = true;
            API.post("/api/bulk-order-imports/" + item.id + "/process")
                .then(() => {
                    this.onSynchronize();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyProcess = false;
                });
        },
        onDelete(item) {
            if (!confirm(this.$t('confirm.delete_bulk_external_order_import'))) {
                return;
            }

            this.isBusyDelete = true;
            API.delete('/api/bulk-order-imports/' + encodeURI(item.id))
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.bulk_external_order.deleted'));
                    this.fetchImports();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        statusColor(status) {
            switch (status) {
                case 'uploaded':
                case 'queued':
                case 'dry-run-finished':
                case 'dry-run-queued':
                    return "primary";
                case 'processing':
                case 'dry-run-processing':
                    return "warning"
                case 'failed':
                case 'validation-failed':
                case 'dry-run-failed':
                case 'dry-run-validation-failed':
                    return "error";
                case 'finished':
                    return "success"
            }
        },
        onDialogClose() {
            this.dialog = false;
        }
    },
    created() {
        this.fetchSynchronization();
    }
}
</script>
